import { ampli } from 'lib-shared-tracking';
import type {
  SearchFilterCondition,
  SearchFilterValueString,
} from 'lib-shared-types-api';
import { useCallback, useMemo } from 'react';

import {
  FilterItemType,
  staticFilterFields,
} from '../../../../../../../utils/definitions';
import { ConditionFieldProps } from '../../../conditionField';

type UseConditionFieldSelectOptions<TCondition extends SearchFilterCondition> =
  {
    condition: TCondition;
    onChange: ConditionFieldProps<TCondition>['onChange'];
  };

type UseConditionFieldSelectResult = {
  handleClear: VoidFunction;
  handleChange: (item: FilterItemType) => void;
  mapValuesToItems: () => FilterItemType[];
};

export function useConditionFieldSelect<
  TCondition extends SearchFilterCondition,
>({
  condition,
  onChange,
}: UseConditionFieldSelectOptions<TCondition>): UseConditionFieldSelectResult {
  const trackValue = useMemo(
    () => staticFilterFields.includes(condition.field),
    [condition.field],
  );

  const handleClear = useCallback(() => {
    ampli.searchFilterValueCleared({
      type: condition.field,
      count: condition.values.length,
    });

    onChange([]);
  }, [condition.field, condition.values.length, onChange]);

  const handleChange = useCallback(
    (item: FilterItemType) => {
      const { name: label, id: value } = item;
      const nextValues = [...condition.values] as SearchFilterValueString[];
      const isNew = !condition.values.some(({ value }) => value === item.id);

      if (isNew) {
        const nextValue: SearchFilterValueString = {
          label,
          value: value.toString(),
        };
        nextValues.push(nextValue);

        ampli.searchFilterValueAdded({
          type: condition.field,
          operator: condition.operator,
          value: trackValue ? nextValue.value : undefined,
        });
      } else {
        nextValues.splice(
          nextValues.findIndex((i) => i.value === value),
          1,
        );
      }

      onChange(nextValues);
    },
    [condition, trackValue, onChange],
  );

  const mapValuesToItems = useCallback(() => {
    return condition.values.map(({ label, value }) => ({
      id: value,
      name: label || value,
    }));
  }, [condition]);

  return { handleChange, handleClear, mapValuesToItems };
}
